import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useCart } from "../context/CartContext";
import { usePortal } from "../context/PortalContext";

const SHOWN_KEY = "cx-exit-shown";
const SKIP_PATHS = ["/checkout", "/admin", "/account"];

// A graceful, honest goodbye — reassurance and help, never pressure or tricks.
// Shown at most once per session, never on transactional pages.
export default function ExitIntent() {
  const { t } = useLanguage();
  const { items } = useCart();
  const { go } = usePortal();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (SKIP_PATHS.some((p) => pathname.startsWith(p))) return;
    if (sessionStorage.getItem(SHOWN_KEY)) return;

    const show = () => {
      if (sessionStorage.getItem(SHOWN_KEY)) return;
      sessionStorage.setItem(SHOWN_KEY, "1");
      setOpen(true);
    };

    // desktop: pointer leaves toward the browser chrome
    const onLeave = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) show();
    };
    // mobile: fast scroll back to the very top = heading for the exit
    let lastY = window.scrollY;
    let lastT = Date.now();
    const onScroll = () => {
      const y = window.scrollY;
      const now = Date.now();
      if (y < 60 && lastY - y > 400 && now - lastT < 600) show();
      lastY = y;
      lastT = now;
    };

    document.addEventListener("mouseout", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onLeave);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  const hasCart = items.length > 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-xir-dark/70 p-6 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="exit-intent"
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-md border border-xir-gold/30 bg-xir-dark p-10 text-center sm:p-12"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              aria-label={t("exit.close")}
              data-testid="exit-close"
              className="absolute end-4 top-4 p-2 text-xir-paper/40 transition-colors hover:text-xir-gold"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <p className="overline-label mb-5">CAFÉXIR</p>
            <p className="font-display text-3xl font-medium leading-snug text-xir-paper" data-testid="exit-title">
              {hasCart ? t("exit.cartTitle") : t("exit.title")}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-xir-paper/55">
              {hasCart ? t("exit.cartSub") : t("exit.sub")}
            </p>
            <div className="mt-9 flex flex-col gap-3">
              {hasCart ? (
                <>
                  <button
                    onClick={() => { setOpen(false); go("/checkout"); }}
                    data-testid="exit-checkout"
                    className="border border-xir-gold bg-xir-gold px-8 py-4 text-xs uppercase tracking-[0.25em] text-xir-dark transition-colors hover:bg-transparent hover:text-xir-gold"
                  >
                    {t("exit.checkout")}
                  </button>
                  <button
                    onClick={() => setOpen(false)}
                    data-testid="exit-continue"
                    className="py-2 text-xs tracking-[0.2em] text-xir-paper/45 transition-colors hover:text-xir-paper"
                  >
                    {t("exit.continue")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { setOpen(false); go("/cupprint"); }}
                    data-testid="exit-quiz"
                    className="border border-xir-gold bg-xir-gold px-8 py-4 text-xs uppercase tracking-[0.25em] text-xir-dark transition-colors hover:bg-transparent hover:text-xir-gold"
                  >
                    {t("exit.quiz")}
                  </button>
                  <button
                    onClick={() => { setOpen(false); go("/shop"); }}
                    data-testid="exit-shop"
                    className="py-2 text-xs tracking-[0.2em] text-xir-paper/45 transition-colors hover:text-xir-paper"
                  >
                    {t("exit.shop")}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
